import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';

const integrations = [
    { icon: 'bi-box-arrow-in-right', title: 'LMS Integration', text: 'Send achievements earned on TC Everywhere back to your LMS — Moodle, Canvas, Blackboard, and more.' },
    { icon: 'bi-code-slash', title: 'RESTful API', text: "Our API lets your internal systems create and issue badges programmatically, query earner data, and push badging events back into your HR or LMS." },
    { icon: 'bi-filetype-xml', title: 'Open Badges Standard', text: 'All badges conform to the IMS Open Badges 2.0+ specification, ensuring full portability between platforms.' },
    { icon: 'bi-plug', title: 'Webhooks', text: 'Subscribe to badge-issued, badge-claimed, and badge-revoked events via webhooks to trigger workflows in your own systems in real time.' },
];

export default function DevelopersPage() {
    return (
        <>
            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 10, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h3" fontWeight={700} gutterBottom>Developers &amp; Integrations</Typography>
                    <Typography variant="h6" sx={{ opacity: 0.85 }}>
                        We want TC Everywhere to work with humans and machines alike.
                        Build on our platform and bring badges into your existing tools.
                    </Typography>
                </Container>
            </Box>

            <Alert severity="warning" icon={false} sx={{ textAlign: 'center', borderRadius: 0, py: 1 }}>
                <Box component="i" className="bi bi-megaphone-fill" sx={{ mr: 1 }} />
                <strong>Full API documentation is coming soon. Stay tuned for updates!</strong>
            </Alert>

            <Box component="section" sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>Integration Options</Typography>
                    <Typography color="text.secondary" textAlign="center" mb={6}>Connect TC Everywhere to the tools your team already uses.</Typography>
                    <Grid container spacing={4}>
                        {integrations.map(({ icon, title, text }) => (
                            <Grid item xs={12} md={6} key={title}>
                                <Card sx={{ height: '100%', p: 2 }}>
                                    <CardContent>
                                        <Box display="flex" gap={2}>
                                            <Box sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: 1, p: 1.5, flexShrink: 0, alignSelf: 'flex-start' }}>
                                                <Box component="i" className={`bi ${icon}`} sx={{ fontSize: '1.25rem' }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="h6" fontWeight={700} gutterBottom>{title}</Typography>
                                                <Typography variant="body2" color="text.secondary">{text}</Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box component="section" sx={{ py: 8, bgcolor: 'background.paper' }}>
                <Container maxWidth="md" sx={{ textAlign: 'center' }}>
                    <Box component="i" className="bi bi-journal-code" sx={{ fontSize: '3rem', color: 'primary.main', mb: 2, display: 'block' }} />
                    <Typography variant="h5" fontWeight={700} gutterBottom>Developer Articles</Typography>
                    <Typography color="text.secondary" mb={3}>
                        Our first article will offer a detailed walkthrough of integrating TC Everywhere
                        into an LMS. Sign up to be notified when it's published.
                    </Typography>
                    <Button component={Link} to="/contact" variant="contained">Notify Me</Button>
                </Container>
            </Box>

            <Box component="section" sx={{ py: 8 }}>
                <Container maxWidth="md">
                    <Typography variant="h5" fontWeight={700} gutterBottom>API Quick Reference</Typography>
                    <Typography color="text.secondary" mb={3}>
                        The TC Everywhere REST API is available to all registered accounts. Authenticate
                        using your API key, available from your account settings once you register.
                    </Typography>
                    <Box sx={{ bgcolor: '#1e1e2e', color: '#e0e0e0', borderRadius: 3, p: 3, fontFamily: 'monospace', fontSize: '0.875rem', mb: 3 }}>
                        <Box sx={{ color: '#6a9955', mb: 1 }}># List public badges</Box>
                        <Box>GET /api/badges?public=true</Box>
                        <Box mt={2} sx={{ color: '#6a9955', mb: 1 }}># Issue a badge</Box>
                        <Box>POST /api/rewards/issue</Box>
                        <Box sx={{ color: 'rgba(255,255,255,0.4)', ml: 2 }}>{`{ "badgeID": "...", "recipients": [{ "email": "user@example.com" }] }`}</Box>
                        <Box mt={2} sx={{ color: '#6a9955', mb: 1 }}># Get issued badges for a user</Box>
                        <Box>GET /api/rewards</Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Full endpoint documentation with request/response schemas is coming soon.
                        In the meantime, <Box component={Link} to="/contact" sx={{ color: 'primary.main' }}>contact us</Box> if you need assistance integrating.
                    </Typography>
                </Container>
            </Box>
        </>
    );
}
